import { Component, HostListener, OnInit } from '@angular/core';
import { AssociationMongo } from 'app/models/association';
import { APPLICATION_TYPE_LABELS, ApplicationType } from 'app/models/application';
import { ApplicationsService } from 'app/services/applications.service';
import { AssociationsService } from 'app/services/associations.service';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { Logger } from 'app/utilities/logger';
import { SessionStorage } from 'app/storage/session.storage';
import { SopStorage } from 'app/storage/sop.storage';
import { AssociationHelper } from 'app/storage/association.storage';
import { catchError, EMPTY, forkJoin, lastValueFrom, of } from 'rxjs';
import { clone } from 'app/utilities/helper';
import { TOPIC_GROUPS } from 'app/models/epe-topics';

enum AssociationStatusFilterEnum {
  all, selected, approved, not_approved, completed, closed
}

const VISIBLE_STATUS = ['selected', 'approved', 'not_approved', 'completed', 'closed'];
const SERVICE_TYPES: ApplicationType[] = ['service1', 'service2'];

@Component({
  selector: 'assigned-service',
  templateUrl: './assigned-service.component.html',
  styleUrls: ['../epes/epes.component.scss', '../association/association.component.scss', './assigned-service.component.scss', '../sop.component.scss']
})
export class AssignedServiceComponent implements OnInit {
  loading = true;
  associations: AssociationMongo[] | null = null;
  allProjects: any[] = [];
  epes: any[] = [];
  selected: string = '';

  AssociationStatusFilter = AssociationStatusFilterEnum;
  filter: AssociationStatusFilterEnum = AssociationStatusFilterEnum.all;

  activeService: ApplicationType = 'service1';
  readonly SERVICE_TYPES = SERVICE_TYPES;
  readonly SERVICE_LABELS = APPLICATION_TYPE_LABELS;

  screenWidth = window.innerWidth;
  @HostListener('window:resize') onResize() { this.screenWidth = window.innerWidth; }

  showAlert = false;
  alertMessage = '';
  alertType: Alert = Alert.SUCCESS;
  search?: string;

  currentEpe?: any;

  constructor(
    private applicationsService: ApplicationsService,
    private serviceMongo: AssociationsService,
    private cache: SopStorage,
    public auth: SessionStorage,
    private logger: Logger,
    private helper: AssociationHelper,
  ) {}

  ngOnInit(): void {
    this.helper.clear();

    forkJoin({
      projects:     this.applicationsService.getAll({ include_user: true }),
      epes:         this.applicationsService.getAll({ type: 'epe', include_user: true }),
      associations: this.serviceMongo.getAll(),
    }).pipe(
      catchError(() => {
        this.addAlert('Error loading assigned services.', Alert.ERROR);
        this.loading = false;
        return EMPTY;
      })
    ).subscribe(then => {
      this.allProjects = (then.projects || [])
        .filter((a: any) => SERVICE_TYPES.includes(a.application_type as ApplicationType))
        .map((app: any) => ({
          ...(app.form_data || {}),
          ...(app.user_info || {}),
          ...app,
          id:             app.user_id ?? app.id,
          application_id: app._id?.['$oid'] ?? app._id,
          sop_status:     app.status,
        }));
      this.cache.setProjects(this.allProjects);

      this.epes = (then.epes || []).map((app: any) => ({
        ...(app.form_data || {}),
        ...(app.user_info || {}),
        ...app,
        id:             app.user_id ?? app.id,
        application_id: app._id?.['$oid'] ?? app._id,
        sop_status:     app.status,
      }));
      this.cache.setEpes(this.epes);

      this.associations = then.associations || [];
      this.helper.setAssociations(this.associations);

      // EPE accounts can only ever hold one 'epe' application (enforced by the
      // backend's 409 guard in create_application), so a single currentEpe is
      // unambiguous. Project accounts CAN hold several applications, so there is
      // deliberately no single "current project" — ownership is decided per row
      // by username in isVisibleForCurrentUser().
      if (this.auth.isEpe()) {
        this.currentEpe = this.epes.find(x => x.user_info?.username === this.auth.getUsername());
        if (this.currentEpe) this.helper.setActiveEpe(this.currentEpe.application_id);
      }

      this.loading = false;
    });
  }

  // ── Service tabs ──────────────────────────────────────────────────────────────

  setService(type: ApplicationType): void {
    this.activeService = type;
    this.selected = '';
    this.filter = AssociationStatusFilterEnum.all;
    this.search = '';
  }

  // ── Status helpers ────────────────────────────────────────────────────────────

  getStatus(project_id: string): string {
    const pa = this.associations?.filter(x => x['project_application_id'] == project_id);
    if (!pa || pa.length === 0) return '';
    for (const s of ['closed', 'completed', 'approved', 'not_approved', 'selected', 'identified', 'open']) {
      if (pa.some(x => x['association_status'] === s)) return s;
    }
    return '';
  }

  isVisibleForCurrentUser(project: any): boolean {
    if (this.auth.isEpe()) {
      if (!this.currentEpe || !this.associations) return false;
      return this.associations.some(a =>
        a['project_application_id'] == project.application_id &&
        a['epe_application_id'] == this.currentEpe.application_id &&
        VISIBLE_STATUS.includes(a['association_status'] as string)
      );
    }
    if (this.auth.isProject()) {
      const username = this.auth.getUsername();
      return !!username && project.user_info?.username === username;
    }
    return false;
  }

  // ── Project list ──────────────────────────────────────────────────────────────

  getProjects(): any[] {
    let list = this.allProjects.filter(p =>
      p.application_type === this.activeService &&
      p.status === 'eligible' &&
      this.isVisibleForCurrentUser(p)
    );

    if (this.search && this.search.length > 0) {
      const q = this.search.toLowerCase();
      list = list.filter(x =>
        (x.username ?? '').toLowerCase().includes(q) ||
        (x.project_acronym ?? '').toLowerCase().includes(q) ||
        ((x.name ?? '') + ' ' + (x.surname ?? '')).toLowerCase().includes(q)
      );
    }

    switch (this.filter) {
      case AssociationStatusFilterEnum.selected:     return list.filter(x => this.getStatus(x.application_id) === 'selected');
      case AssociationStatusFilterEnum.approved:     return list.filter(x => this.getStatus(x.application_id) === 'approved');
      case AssociationStatusFilterEnum.not_approved: return list.filter(x => this.getStatus(x.application_id) === 'not_approved');
      case AssociationStatusFilterEnum.completed:    return list.filter(x => this.getStatus(x.application_id) === 'completed');
      case AssociationStatusFilterEnum.closed:       return list.filter(x => this.getStatus(x.application_id) === 'closed');
      default: return list;
    }
  }

  // ── EPEs for a project (used by association-sopcard-small) ────────────────────

  getProjectEpes(project_id: string): any[] {
    if (!this.associations) return [];
    let assocs = this.associations.filter(x => x['project_application_id'] == project_id);
    if (this.auth.isEpe() && this.currentEpe) {
      assocs = assocs.filter(x => x['epe_application_id'] == this.currentEpe.application_id);
    }
    const epeIds = assocs.map(x => x['epe_application_id']);
    return [...this.epes]
      .filter(x => epeIds.includes(x.application_id))
      .map(x => {
        x['association_status'] = this.associations!.find(
          y => y['epe_application_id'] === x.application_id && y['project_application_id'] === project_id
        )?.['association_status'];
        return x;
      });
  }

  // ── Expand ────────────────────────────────────────────────────────────────────

  expand(id: string): void {
    if (id === this.selected) { this.selected = ''; return; }
    const pa = this.associations?.filter(x => x['project_application_id'] == id) || [];

    let epeId: any = null;
    if (this.auth.isEpe() && this.currentEpe) {
      epeId = this.currentEpe.application_id;
    } else {
      epeId = pa.find(x => VISIBLE_STATUS.includes(x['association_status'] as string))?.['epe_application_id'] ?? null;
    }
    this.helper.setSelectedEpe(epeId ?? '');

    const meeting = pa.find(x => x['epe_application_id'] == epeId && x['project_application_id'] == id)?.meeting;
    if (meeting) this.helper.setMeetingForm(meeting as number);
    this.helper.setSelectedProject(id);
    this.selected = id;
  }

  reloadAssociations(): void {
    this.serviceMongo.getAll().pipe(
      catchError(() => {
        this.addAlert('Error reloading associations.', Alert.ERROR);
        return EMPTY;
      })
    ).subscribe((a: AssociationMongo[]) => {
      this.associations = a;
      this.helper.setAssociations(a);
    });
  }

  // ── Topic labels ──────────────────────────────────────────────────────────────

  getTopicLabels(project: any): string[] {
    const topics: string[] = project.form_data?.topics || project.topics || [];
    const labels: string[] = [];
    for (const value of topics) {
      if (typeof value !== 'string') continue;
      let found = false;
      for (const group of TOPIC_GROUPS) {
        for (const sub of group.subGroups) {
          if (sub.value === value) { labels.push(`${group.label} > ${sub.label}`); found = true; break; }
          const child = sub.children.find(c => c.value === value);
          if (child) { labels.push(`${group.label} > ${sub.label} > ${child.label}`); found = true; break; }
        }
        if (found) break;
      }
      if (!found) labels.push(value);
    }
    return labels;
  }

  // ── Status UI helpers ─────────────────────────────────────────────────────────

  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      '': 'Open', open: 'Open', identified: 'Identified', selected: 'Selected',
      approved: 'Approved', not_approved: 'Not Approved', completed: 'Completed', closed: 'Closed',
    };
    return map[status] || status;
  }

  getStatusBadgeClass(status: string): string {
    const map: Record<string, string> = {
      '': 'label-default', open: 'label-default', identified: 'label-info',
      selected: 'label-primary', approved: 'label-success', not_approved: 'label-danger',
      completed: 'label-warning', closed: 'label-default',
    };
    return map[status] || 'label-default';
  }

  getBtnText(project_id: string): string {
    const s = this.getStatus(project_id);
    if (this.auth.isEpe() && s === 'selected') return 'Accept / Decline';
    const map: Record<string, string> = {
      selected: 'View Selected', approved: 'Go to Service',
      not_approved: 'View details', completed: 'View details', closed: 'View details',
    };
    return map[s] || 'View';
  }

  changeFilter(s: AssociationStatusFilterEnum): void { this.filter = s; }

  addAlert(message: string, type: Alert): void {
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;
  }
}
